import { endpoint } from './endpoints';
import { fieldType } from 'app/area/search-filters/constants';
import { CustomFilterType, Filter, FilterCommonValues, FilterField, FilterType } from 'app/area/search-filters/filters/types';
import { ExtraInfoCodeValues, ExtraInfoType } from 'app/components/access-panel/shared/types';
import { DefaultContainerProps } from 'app/infrastructure/redux';
import { getExtraInfoFilterId } from 'app/area/administration/shared/helpers';
import { ExtraInfoFilterId } from 'app/area/administration/shared/types';
import { ExtraInfoApplyRoleAccess } from 'app/area/administration/list/types';
import { SearchTranslation } from 'app/area/administration/list/constants';
import { getFieldId } from 'app/area/search-filters/filters/selectors';

export const shouldLoadExtraInfo = (props: DefaultContainerProps): boolean =>
  props.sharedState?.access?.adminPanel?.extraInfoPanel;

export const shouldLoadRoles = (props: DefaultContainerProps): boolean =>
  props.sharedState?.access?.adminPanel?.user?.administrationCentral || props.sharedState?.access?.adminPanel?.user?.read;

export const applyRoleAccessConfig = (applyRoleAccess: ExtraInfoApplyRoleAccess) => applyRoleAccess?.shouldDisplay && {
  fieldTypeId: fieldType.excludeInherited,
  type: CustomFilterType.checkbox,
  includeInSummary: false,
  options: [{ value: 1, title: applyRoleAccess?.title }]
};

export const createFilterFields = (filter: FilterField, applyRoleAccess: ExtraInfoApplyRoleAccess): FilterField[] =>
  [filter, applyRoleAccessConfig(applyRoleAccess)].filter(field => !!field);

export const extraInfoTextFilter = (extraInfoCode: ExtraInfoCodeValues, applyRoleAccess: ExtraInfoApplyRoleAccess): Filter => ({
  id: getExtraInfoFilterId(extraInfoCode.id, ExtraInfoFilterId.string),
  group: 'extraInfo',
  label: extraInfoCode.description,
  fields: createFilterFields({
    fieldTypeId: fieldType.root,
    type: CustomFilterType.text,
    includeInSummary: true
  }, applyRoleAccess),
  type: FilterType.TEXT
});

export const extraInfoDateFilter = (extraInfoCode: ExtraInfoCodeValues, applyRoleAccess: ExtraInfoApplyRoleAccess): Filter => ({
  id: getExtraInfoFilterId(extraInfoCode.id, ExtraInfoFilterId.date),
  group: 'extraInfo',
  label: extraInfoCode.description,
  fields: createFilterFields({
    fieldTypeId: fieldType.date,
    type: CustomFilterType.dateRange,
    includeInSummary: true
  }, applyRoleAccess),
  type: FilterType.DATE_RANGE
});

export const extraInfoNumberFilter = (extraInfoCode: ExtraInfoCodeValues, applyRoleAccess: ExtraInfoApplyRoleAccess): Filter => ({
  id: getExtraInfoFilterId(extraInfoCode.id, ExtraInfoFilterId.integer),
  group: 'extraInfo',
  label: extraInfoCode.description,
  fields: createFilterFields({
    fieldTypeId: fieldType.root,
    type: CustomFilterType.inputIntegerFilter,
    includeInSummary: true
  }, applyRoleAccess),
  type: FilterType.TEXT_RANGE
});

export const extraInfoFloatFilter = (extraInfoCode: ExtraInfoCodeValues, applyRoleAccess: ExtraInfoApplyRoleAccess): Filter => ({
  id: getExtraInfoFilterId(extraInfoCode.id, ExtraInfoFilterId.float),
  group: 'extraInfo',
  label: extraInfoCode.description,
  fields: createFilterFields({
    fieldTypeId: fieldType.number,
    type: CustomFilterType.decimalRange,
    includeInSummary: true
  }, applyRoleAccess),
  type: FilterType.DECIMAL_RANGE
});

export const extraInfoMultiSelectFilter = (extraInfoCode: ExtraInfoCodeValues, applyRoleAccess: ExtraInfoApplyRoleAccess): Filter => ({
  id: getExtraInfoFilterId(extraInfoCode.id, ExtraInfoFilterId.code),
  group: 'extraInfo',
  label: extraInfoCode.description,
  fields: createFilterFields({
    fieldTypeId: fieldType.root,
    type: extraInfoCode.isHierarchy ? CustomFilterType.codeHierarchy : CustomFilterType.code,
    optionsEndpoint: endpoint.extraInfoMultiSelect(extraInfoCode.id),
    optionsParams: { displayNotSelected: true, categoryId: extraInfoCode.category },
    isHierarchy: extraInfoCode.isHierarchy
  }, applyRoleAccess),
  type: FilterType.SELECTED_IDS
});

export const configExtraInfoFilters = (extraInfoCode: ExtraInfoCodeValues, applyRoleAccess: ExtraInfoApplyRoleAccess): Filter => ({
  [ExtraInfoType.text]: extraInfoTextFilter(extraInfoCode, applyRoleAccess),
  [ExtraInfoType.date]: extraInfoDateFilter(extraInfoCode, applyRoleAccess),
  [ExtraInfoType.integer]: extraInfoNumberFilter(extraInfoCode, applyRoleAccess),
  [ExtraInfoType.float]: extraInfoFloatFilter(extraInfoCode, applyRoleAccess),
  [ExtraInfoType.singleSelect]: extraInfoMultiSelectFilter(extraInfoCode, applyRoleAccess),
  [ExtraInfoType.multiSelect]: extraInfoMultiSelectFilter(extraInfoCode, applyRoleAccess)
}[extraInfoCode.extraInfoDataType]);

export const mapExtraInfoFilters = (extraInfoCodes: ExtraInfoCodeValues[], applyRoleAccess: ExtraInfoApplyRoleAccess): Filter[] =>
  extraInfoCodes
    ?.map(extraInfoCode => configExtraInfoFilters(extraInfoCode, applyRoleAccess))
    ?.filter(f => !!f);

export const extraInfoFilters = (
  filters: Filter[] = [],
  extraInfoCodes: ExtraInfoCodeValues[],
  applyRoleAccess: ExtraInfoApplyRoleAccess): Filter[] =>
  [...filters, ...mapExtraInfoFilters(extraInfoCodes, applyRoleAccess)];

export const extraInfoDefaultFilters = (
  extraInfoCodes: ExtraInfoCodeValues[],
  translation: SearchTranslation
): { [key: string]: FilterCommonValues<number>[] } =>
  mapExtraInfoFilters(extraInfoCodes, { shouldDisplay: true, title: translation?.exclude_inherited_from_role })
    ?.map(value => getFieldId(value?.id, fieldType.excludeInherited))
    ?.reduce((acc, curr) => (acc[curr] = [{ value: 1, title: translation?.exclude_inherited_from_role }], acc), {}) ?? {};
